/**
 * OrderDetailWidget — voice mode rich order-detail card (D-76).
 *
 * Renders the single-order deep-dive emitted by `get_order_details`. Card
 * sections: header (status pill + order id + headline + retailer chip + "View
 * on {retailer}" link), items (image + brand + title + subtitle + price-with-
 * strikethrough + reward chip + return pill), shipping (recipient + address +
 * method/eta + optional tracking link), payment (issuer badge + last4 + billed-
 * to-same), order summary (subtotal / shipping / tax / fees / total).
 *
 * Theming:
 *  - Brand-overridable surfaces (`--voice-widget-card-bg`, `--voice-widget-border`,
 *    `--voice-widget-fg`, `--voice-widget-muted`, `--voice-widget-img-bg`,
 *    `--voice-widget-accent-dark`) flow through from `VoiceThemeProvider`
 *    (D-56) so brand Identity colors apply.
 *  - Status pill palette is hardcoded as SEMANTIC color (green for delivered,
 *    amber for processing, red for cancelled, etc.) — matches the
 *    `OrderListWidget` precedent (D-75). Brand themes only override the
 *    accent/fg/button tokens, not status semantics.
 *
 * Source mock: voice-chat-wiki/mocks/order-detail-card-mock.tsx. The mock's
 * top-of-page buttons (order switcher / Details toggle) are intentionally
 * ignored — only the card body is reproduced here.
 */
import React, { useState } from 'react';
import { CornerUpLeft, CreditCard, ExternalLink, Gift, Package, Truck } from 'lucide-react';
import type {
  OrderDetailItem,
  OrderDetailPayment,
  OrderDetailShipping,
  OrderDetailSummary,
  OrderDetailWidgetData,
  OrderStatus,
} from '../../types/widgets';

const WIDGET_KEYFRAMES = `
@keyframes voice-order-detail-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

// ─── Status pill (semantic palette — see header comment) ─────────────────────

const STATUS_PILL: Record<OrderStatus, { label: string; bg: string; fg: string; dot: string }> = {
  placed: { label: 'Placed', bg: '#F1EFE8', fg: '#444441', dot: '#888780' },
  processing: { label: 'Processing', bg: '#FAEEDA', fg: '#633806', dot: '#BA7517' },
  shipped: { label: 'Shipped', bg: '#E6F1FB', fg: '#0C447C', dot: '#185FA5' },
  out_for_delivery: { label: 'Out for delivery', bg: '#E6F1FB', fg: '#0C447C', dot: '#185FA5' },
  delivered: { label: 'Delivered', bg: '#E8F3E4', fg: '#1F4711', dot: '#3B6D11' },
  cancelled: { label: 'Cancelled', bg: '#FCEBEB', fg: '#791F1F', dot: '#A32D2D' },
  returned: { label: 'Returned', bg: '#F1EFE8', fg: '#444441', dot: '#888780' },
  unknown: { label: 'Unknown', bg: '#F1EFE8', fg: '#444441', dot: '#888780' },
};

// Shared semantic accents used by the per-item return pill + reward chip. Same
// philosophy as the status pill: semantic colors, not brand colors.
const RETURN_PILL_BG = '#FAEEDA';
const RETURN_PILL_FG = '#633806';
const RETURN_PILL_FG_MUTED = '#854F0B';
const REWARD_CHIP_BG = '#EEEDFE';
const REWARD_CHIP_FG = '#26215C';
const SHIPPING_FREE_FG = '#3B6D11';

// ─── Themeable surfaces (flow from VoiceThemeProvider) ───────────────────────

const SURFACE = 'var(--voice-widget-card-bg, #FFFFFF)';
const BORDER = 'var(--voice-widget-border, #E5E7EB)';
const FG = 'var(--voice-widget-fg, #111827)';
const MUTED = 'var(--voice-widget-muted, #6B7280)';
const IMG_BG = 'var(--voice-widget-img-bg, #F9FAFB)';
const ACCENT_DARK = 'var(--voice-widget-accent-dark, #0D9488)';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', CAD: '$', EUR: '€', GBP: '£' };

function money(amount: number | undefined, currency: string = 'USD'): string | undefined {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return undefined;
  const symbol = CURRENCY_SYMBOL[currency] ?? '';
  return `${symbol}${Number(amount).toFixed(2)}`;
}

function formatPlacedAt(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeIssuerLabel(issuer: string | undefined): string | undefined {
  if (!issuer) return undefined;
  const v = issuer.trim().toLowerCase();
  if (v === 'visa') return 'VISA';
  if (v === 'mastercard' || v === 'master' || v === 'master-card') return 'MASTERCARD';
  if (v === 'american-express' || v === 'amex' || v === 'american_express') return 'AMEX';
  if (v === 'discover') return 'DISCOVER';
  return issuer.toUpperCase();
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ItemImage({ src, alt }: { src?: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  const showPlaceholder = !src || errored;
  return (
    <div
      style={{
        width: 88,
        height: 88,
        flexShrink: 0,
        background: IMG_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {showPlaceholder ? (
        <Package size={28} strokeWidth={1.5} color={MUTED} aria-hidden />
      ) : (
        <img
          src={src}
          alt={alt}
          onError={() => setErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      )}
    </div>
  );
}

function SectionLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: MUTED,
        marginBottom: 10,
      }}
    >
      {icon}
      {text}
    </div>
  );
}

function SummaryRow({
  label,
  value,
  valueColor,
  valueWeight,
}: {
  label: string;
  value?: string;
  valueColor?: string;
  valueWeight?: number;
}) {
  if (value === undefined) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <span style={{ color: MUTED }}>{label}</span>
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: valueColor ?? FG,
          fontWeight: valueWeight ?? 400,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ItemRow({
  item,
  currency,
  isLast,
}: {
  item: OrderDetailItem;
  currency: string;
  isLast: boolean;
}) {
  const itemCurrency = item.currency ?? currency;
  const showStrike =
    item.currentRetailPrice !== undefined && item.currentRetailPrice !== item.pricePaid;
  const subtitleBits = [item.subtitle, item.qty ? `Qty ${item.qty}` : undefined].filter(Boolean) as string[];
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        padding: '20px 24px',
        borderBottom: isLast ? undefined : `1px solid ${BORDER}`,
      }}
    >
      <ItemImage src={item.imageUrl} alt={item.title} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.brand && (
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: MUTED,
              marginBottom: 4,
            }}
          >
            {item.brand}
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 500, color: FG, lineHeight: 1.35 }}>{item.title}</div>
        {(subtitleBits.length > 0 || item.sellerName) && (
          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>
            {subtitleBits.join(' · ')}
            {item.sellerName && (
              <>
                {subtitleBits.length > 0 && <span style={{ margin: '0 6px', color: BORDER }}>·</span>}
                <span>Sold by {item.sellerName}</span>
              </>
            )}
          </div>
        )}

        {/* Price row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '4px 10px',
            marginTop: 10,
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 600, color: FG, fontVariantNumeric: 'tabular-nums' }}>
            {money(item.pricePaid, itemCurrency)}
          </span>
          {showStrike && (
            <span
              style={{
                fontSize: 12.5,
                color: MUTED,
                textDecoration: 'line-through',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {money(item.currentRetailPrice, itemCurrency)}
            </span>
          )}
          {item.rewardEarned !== undefined && item.rewardEarned > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 500,
                color: REWARD_CHIP_FG,
                background: REWARD_CHIP_BG,
                padding: '2px 8px',
                borderRadius: 999,
              }}
            >
              <Gift size={12} strokeWidth={2} aria-hidden />
              {money(item.rewardEarned, itemCurrency)} reward earned
            </span>
          )}
        </div>

        {item.shipmentStatus && (
          <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
            {item.shipmentStatusMessage ?? item.shipmentStatus}
          </div>
        )}

        {item.returnStatus && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 12,
              fontSize: 12,
              fontWeight: 500,
              color: RETURN_PILL_FG,
              background: RETURN_PILL_BG,
              padding: '4px 10px',
              borderRadius: 6,
            }}
          >
            <CornerUpLeft size={13} strokeWidth={2} aria-hidden />
            {item.returnStatus.label}
            {item.returnStatus.reason && (
              <span style={{ fontWeight: 400, color: RETURN_PILL_FG_MUTED }}>· {item.returnStatus.reason}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ShippingSection({ shipping }: { shipping: OrderDetailShipping }) {
  const cityLine = [shipping.city, shipping.state].filter(Boolean).join(', ');
  return (
    <div style={{ background: SURFACE, padding: '20px 24px' }}>
      <SectionLabel
        icon={<Truck size={13} strokeWidth={2} color={MUTED} aria-hidden />}
        text={shipping.isPickup ? 'Pickup' : 'Shipping'}
      />
      <div style={{ fontSize: 13, lineHeight: 1.55, color: FG }}>
        {shipping.recipientName && <div>{shipping.recipientName}</div>}
        {shipping.addressLine1 && <div>{shipping.addressLine1}</div>}
        {shipping.addressLine2 && <div>{shipping.addressLine2}</div>}
        {(cityLine || shipping.zipCode) && (
          <div>
            {cityLine}
            {shipping.zipCode ? ` ${shipping.zipCode}` : ''}
          </div>
        )}
        {shipping.country && <div>{shipping.country}</div>}
      </div>
      {(shipping.method || shipping.etaLabel) && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>
          {[shipping.method, shipping.etaLabel].filter(Boolean).join(' · ')}
        </div>
      )}
      {shipping.trackingUrl && (
        <a
          href={shipping.trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            marginTop: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 500,
            color: ACCENT_DARK,
            textDecoration: 'none',
          }}
        >
          Track{shipping.carrier ? ` with ${shipping.carrier}` : ''}
          <ExternalLink size={12} strokeWidth={2} aria-hidden />
        </a>
      )}
    </div>
  );
}

function PaymentSection({ payment, currency }: { payment: OrderDetailPayment; currency: string }) {
  const issuerLabel = normalizeIssuerLabel(payment.issuer);
  return (
    <div style={{ background: SURFACE, padding: '20px 24px' }}>
      <SectionLabel
        icon={<CreditCard size={13} strokeWidth={2} color={MUTED} aria-hidden />}
        text="Payment"
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: FG }}>
        {issuerLabel && (
          <span
            style={{
              background: FG,
              color: SURFACE,
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}
          >
            {issuerLabel}
          </span>
        )}
        {payment.last4 && <span>ending in {payment.last4}</span>}
        {!issuerLabel && !payment.last4 && payment.methodLabel && <span>{payment.methodLabel}</span>}
      </div>
      {payment.cardHolderName && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{payment.cardHolderName}</div>
      )}
      {payment.billedToSame && <div style={{ fontSize: 12, color: MUTED, marginTop: 10 }}>Billed to same address</div>}
      {payment.giftCardApplied !== undefined && payment.giftCardApplied > 0 && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
          Gift card applied: {money(payment.giftCardApplied, currency)}
        </div>
      )}
    </div>
  );
}

function SummarySection({ summary, currency }: { summary: OrderDetailSummary; currency: string }) {
  return (
    <div style={{ background: IMG_BG, padding: '20px 24px', borderTop: `1px solid ${BORDER}` }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: MUTED,
          marginBottom: 12,
        }}
      >
        Order summary
      </div>
      <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SummaryRow label="Subtotal" value={money(summary.subtotal, currency)} />
        {summary.shipping !== undefined && (
          <SummaryRow
            label="Shipping"
            value={summary.shipping === 0 ? 'Free' : money(summary.shipping, currency)}
            valueColor={summary.shipping === 0 ? SHIPPING_FREE_FG : undefined}
            valueWeight={summary.shipping === 0 ? 500 : undefined}
          />
        )}
        {summary.shippingDiscount !== undefined && summary.shippingDiscount > 0 && (
          <SummaryRow label="Shipping discount" value={`−${money(summary.shippingDiscount, currency)}`} />
        )}
        {summary.couponDiscount !== undefined && summary.couponDiscount > 0 && (
          <SummaryRow label="Coupon" value={`−${money(summary.couponDiscount, currency)}`} />
        )}
        {summary.giftCard !== undefined && summary.giftCard > 0 && (
          <SummaryRow label="Gift card" value={`−${money(summary.giftCard, currency)}`} />
        )}
        {summary.tax !== undefined && <SummaryRow label="Tax" value={money(summary.tax, currency)} />}
        {summary.serviceFee !== undefined && summary.serviceFee > 0 && (
          <SummaryRow label="Service fee" value={money(summary.serviceFee, currency)} />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, color: FG }}>Total</span>
        <span style={{ fontSize: 19, fontWeight: 600, color: FG, fontVariantNumeric: 'tabular-nums' }}>
          {money(summary.total, currency)}
        </span>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export interface OrderDetailWidgetProps {
  data: OrderDetailWidgetData;
}

export const OrderDetailWidget: React.FC<OrderDetailWidgetProps> = ({ data }) => {
  const currency = data.summary?.currency ?? 'USD';
  const pill = STATUS_PILL[data.status] ?? STATUS_PILL.unknown;
  const placedAt = formatPlacedAt(data.placedAt);
  const hasShipping = !!data.shipping;
  const hasPayment = !!data.payment;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 640,
        maxHeight: '100%',
        boxSizing: 'border-box',
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        animation: 'voice-order-detail-fade-in 0.28s ease-out both',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{WIDGET_KEYFRAMES}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '20px 24px',
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 500,
                letterSpacing: '0.02em',
                background: pill.bg,
                color: pill.fg,
              }}
            >
              <span
                style={{ width: 6, height: 6, borderRadius: '50%', background: pill.dot, display: 'inline-block' }}
                aria-hidden
              />
              {pill.label}
            </span>
            <span style={{ fontSize: 11.5, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>#{data.orderId}</span>
          </div>
          {data.headline && (
            <div style={{ fontSize: 21, fontWeight: 600, color: FG, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
              {data.headline}
            </div>
          )}
          {data.subHeadline && <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.4 }}>{data.subHeadline}</div>}
          {(placedAt || data.retailerName) && (
            <div style={{ fontSize: 13, color: MUTED, marginTop: 8 }}>
              {placedAt && <span>Placed {placedAt}</span>}
              {placedAt && data.retailerName && <span style={{ margin: '0 6px', color: BORDER }}>·</span>}
              {data.retailerName && <span>{data.retailerName}</span>}
            </div>
          )}
        </div>

        {data.retailerOrderPageUrl && (
          <a
            href={data.retailerOrderPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: `1px solid ${BORDER}`,
              background: SURFACE,
              color: FG,
              fontSize: 12,
              fontWeight: 500,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            View on {data.retailerName ?? 'retailer'}
            <ExternalLink size={12} strokeWidth={2} aria-hidden />
          </a>
        )}
      </div>

      {/* ── Items ───────────────────────────────────────────────────────── */}
      <div
        style={{
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {data.items.map((item, i) => (
          <ItemRow key={i} item={item} currency={currency} isLast={i === data.items.length - 1} />
        ))}
      </div>

      {/* ── Shipping + Payment ──────────────────────────────────────────── */}
      {(hasShipping || hasPayment) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: hasShipping && hasPayment ? '1fr 1fr' : '1fr',
            gap: 1,
            background: BORDER,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {hasShipping && data.shipping && <ShippingSection shipping={data.shipping} />}
          {hasPayment && data.payment && <PaymentSection payment={data.payment} currency={currency} />}
        </div>
      )}

      {/* ── Order summary ───────────────────────────────────────────────── */}
      <SummarySection summary={data.summary} currency={currency} />
    </div>
  );
};

export default OrderDetailWidget;
